import React, { useMemo } from 'react';
import { BodyTextSmall, BodyText, FlexCol, GridDynamic } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { ProjectFieldRestrictionFieldOption } from '@minecraft.graphql-operations';

export type CreativeTeamFieldValues = {
  adAgency?: string;
  director?: string;
  executiveProducer?: string;
  network?: string;
  photographer?: string;
  producer?: string;
  productionCompany?: string;
  studio?: string;
  writer?: string;
};

type CreativeTeamProps = {
  fieldValues: CreativeTeamFieldValues;
};

const LabelAndValue = ({ label, value, testId }: { label: string; value: string; testId: string }) => {
  if (!value) {
    return null;
  }

  return (
    <FlexCol w="full" gap="1" wrap="nowrap" data-testid={testId}>
      <BodyTextSmall fontWeight="medium" color="disabled">
        {label}
      </BodyTextSmall>
      <BodyText className="cn_white-space-pre-wrap">{value}</BodyText>
    </FlexCol>
  );
};

export const CreativeTeamLabelAndValue = ({
  labels,
  fieldName,
  fieldValues,
}: CreativeTeamProps & {
  labels: Record<ProjectFieldRestrictionFieldOption, string>;
  fieldName: string;
}) => {
  const value = fieldValues[fieldName];

  if (!value || value === '') {
    return null;
  }

  return <LabelAndValue label={labels?.[fieldName]} value={value} testId={`creative-team-${fieldName}`} />;
};

const CREATIVE_TEAM_FIELDS = Object.values(ProjectFieldRestrictionFieldOption);

export const CreativeTeam = ({ fieldValues }: CreativeTeamProps) => {
  const { t } = useTranslation();
  const labels: Record<ProjectFieldRestrictionFieldOption, string> = useMemo(
    () => ({
      adAgency: t('common:creativeTeam.adAgency'),
      director: t('common:creativeTeam.director'),
      executiveProducer: t('common:creativeTeam.executiveProducer'),
      network: t('common:creativeTeam.network'),
      photographer: t('common:creativeTeam.photographer'),
      producer: t('common:creativeTeam.producer'),
      productionCompany: t('common:creativeTeam.productionCompany'),
      studio: t('common:creativeTeam.studio'),
      writer: t('common:creativeTeam.writer'),
    }),
    [t]
  );

  return (
    <GridDynamic
      w="full"
      columns="1fr"
      mdProps={{ columns: 'repeat(2, 1fr)' }}
      lgProps={{ columns: 'repeat(3, 1fr)' }}
      gap="6"
      data-testid="creative-team-values"
    >
      {CREATIVE_TEAM_FIELDS.map((fieldName) => (
        <CreativeTeamLabelAndValue key={fieldName} fieldValues={fieldValues} labels={labels} fieldName={fieldName} />
      ))}
    </GridDynamic>
  );
};
